import React, { useEffect, useState, useCallback } from "react";
import { trackFeatureAction } from "../../../analytics/googleAnalytics";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAutoRepeatData,
  fetchFilteredAutoRepeatData,
  fetchFullAppraisalData,
  fetchFilteredFullAppraisalData,
  fetchTopUpData,
  fetchFilteredTopUpData,
  fetchServicingBAList,
  updateServicingBA,
  resetServicingBAState,
} from '../../../redux/slice/fetchRepeatEligibilityData';
import { downloadRepeatEligibilityReport } from "../../../redux/slice/downloadRepeatEligibility";
import { HashLoader } from "react-spinners";
import { Card, Button, Input, Select, Space, Table, Alert, Empty, Modal, Form, message } from "antd";
import { StyledTable, StyledButton } from "../components/styleComp";
import { useAuth } from "../../../context/AuthContext";
import jwt_decode from "jwt-decode";

const { Option } = Select;

const RepeatEligibility = (props) => {
  const { authState } = useAuth();
  const dispatch = useDispatch();

  const [state, setState] = useState({
    pages: { autoRepeat: { page: 1, pageSize: 7 }, fullAppraisal: { page: 1, pageSize: 7 }, topUp: { page: 1, pageSize: 7 } },
    filters: { autoRepeat: {}, fullAppraisal: {}, topUp: {} },
    searchInputs: { autoRepeat: "", fullAppraisal: "", topUp: "" },
    selectedColumns: { autoRepeat: "", fullAppraisal: "", topUp: "" },
    hasSearched: { autoRepeat: false, fullAppraisal: false, topUp: false },
  });

  const [triggerFilter, setTriggerFilter] = useState(null);

  const [baModal, setBaModal] = useState({ open: false, record: null, type: null });
  const [selectedBA, setSelectedBA] = useState(undefined);
  const [remarks, setRemarks] = useState("");

  const isAdmin = String(props.userDetails?.role || "") === "TS";
  console.log("ROLE - ", props.userDetails?.role || "");
  const jwtBearer = authState.token;
  const tokenDecoded = jwtBearer ? jwt_decode(jwtBearer) : null;
  const jwtPayload = tokenDecoded ? {
    group: props.userDetails.group_id,
    user: tokenDecoded.user_id,
    role: props.userDetails.role,
    expiry: tokenDecoded.exp,
  } : {};

  useEffect(() => {
    if (jwtBearer) {
      const { page, pageSize } = state.pages.autoRepeat;
      dispatch(fetchAutoRepeatData({ jwtBearer, page, filters: state.filters.autoRepeat, pageSize }));
    }
  }, [jwtBearer, state.pages.autoRepeat, state.filters.autoRepeat, dispatch]);

  useEffect(() => {
    if (jwtBearer) {
      const { page, pageSize } = state.pages.fullAppraisal;
      dispatch(fetchFullAppraisalData({ jwtBearer, page, filters: state.filters.fullAppraisal, pageSize }));
    }
  }, [jwtBearer, state.pages.fullAppraisal, state.filters.fullAppraisal, dispatch]);


  useEffect(() => {
    if (jwtBearer) {
      const { page, pageSize } = state.pages.topUp;
      dispatch(fetchTopUpData({ jwtBearer, page, filters: state.filters.topUp, pageSize }));
    }
  }, [jwtBearer, state.pages.topUp, state.filters.topUp, dispatch]);

  const {
    autoRepeatData,
    fullAppraisalData,
    topUpData,
    filteredAutoRepeatData,
    filteredFullAppraisalData,
    filteredTopUpData,
    totalCountAutoRepeat,
    totalCountFullAppraisal,
    totalCountTopUp,
    isLoadingAutoRepeat,
    isLoadingFullAppraisal,
    isLoadingTopUp,
    isLoadingFilteredAutoRepeat,
    isLoadingFilteredFullAppraisal,
    isLoadingFilteredTopUp,
    errorAutoRepeat,
    errorFullAppraisal,
    errorTopUp,
  } = useSelector((state) => state.repeatEligibility);

  const {
    servicingBAList,
    isLoadingServicingBAList,
    errorServicingBAList,
    isUpdatingServicingBA,
    updateServicingBAError,
    updateServicingBASuccess,
  } = useSelector((state) => state.repeatEligibility);

  const {
    isDownloadingAuto,
    isErrorAutoDownload,
    isDownloadingFull,
    isErrorFullDownload,
    isDownloadingTop,
    isErrorTopUpDownload,
  } = useSelector((state) => state.downloadRepeatEligibility);

  const defineColumns = useCallback((data, type) => {
    if (data.length === 0) return [];
    const keys = Object.keys(data[0]);
    const baseCols = keys.map((key) => ({
      title: <strong style={{ fontSize: "18px" }}>{key.replace(/_/g, " ").toUpperCase()}</strong>,
      dataIndex: key,
      key: key,
      render: (text) => <span style={{ fontSize: "16px" }}>{text}</span>,
    }));

    if (isAdmin && (type === "fullAppraisal" || type === "topUp")) {
      baseCols.push({
        title: <strong style={{ fontSize: "18px" }}>ACTION</strong>,
        key: "action",
        fixed: "right",
        width: 160,
        render: (_, record) => (
          <Button
            type="primary"
            size="small"
            onClick={() => handleOpenBAModal(record, type)}
          >
            Update BA
          </Button>
        ),
      });
    }
    return baseCols;
  }, [isAdmin]);

  const getLoanExtId = (record) =>
    record?.application_number || record?.APPLICATION_NUMBER || record?.LOAN_EXT_ID || record?.loan_ext_id;
  const getBranchCode = (record) => {
    const raw = record?.BRANCH || record?.branch || record?.BRANCH_CODE || record?.branch_code || record?.Branch_Code || record?.BranchCode;
    if (!raw) return undefined;
    return String(raw).split("-")[0].trim();
  };

  const handleOpenBAModal = (record, type) => {
    trackFeatureAction(
      "Reports",
      "Repeat Eligibility",
      "open",
      {
        context: "servicing_ba"
      }
    );
    setBaModal({ open: true, record, type });
    setSelectedBA(undefined);
    setRemarks("");
    dispatch(resetServicingBAState());
    const branch_code = getBranchCode(record);
    if (branch_code) {
      dispatch(fetchServicingBAList({ jwtBearer, branch_code }));
    } else {
      message.error("Branch code not available on this record");
    }
  };

  const handleCloseBAModal = () => {
    setBaModal({ open: false, record: null, type: null });
    setSelectedBA(undefined);
    setRemarks("");
    dispatch(resetServicingBAState());
  };

  const handleSubmitBA = async () => {
    trackFeatureAction(
      "Reports",
      "Repeat Eligibility",
      "submit",
      {
        action_type: "servicing_ba"
      }
    );
    if (!selectedBA) {
      message.warning("Please select a Servicing BA");
      return;
    }
    const record = baModal.record;
    const loan_ext_id = getLoanExtId(record);
    if (!loan_ext_id) {
      message.error("Loan Ext ID not available on this record");
      return;
    }
    const [empId, ...nameParts] = selectedBA.split(" - ");
    const empName = nameParts.join(" - ");
    const result = await dispatch(
      updateServicingBA({
        jwtBearer,
        payload: {
          loan_ext_id,
          new_servicing_emp_id: empId,
          new_servicing_emp_name: empName,
          remarks,
        },
      })
    );
    if (updateServicingBA.fulfilled.match(result)) {
      message.success("Servicing BA updated successfully");
      handleCloseBAModal();
      const { page, pageSize } = state.pages[baModal.type];
      const refetch = baModal.type === "fullAppraisal" ? fetchFullAppraisalData : fetchTopUpData;
      dispatch(refetch({ jwtBearer, page, pageSize, filters: state.filters[baModal.type] }));
    } else {
      message.error(result.payload || "Failed to update Servicing BA");
    }
  };

  const handleKeyDown = (event, type) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleFilter(type);
    }
  };

  const handleFilter = (type) => {
    trackFeatureAction("Reports", "Repeat Eligibility", "search");
    const filterActions = {
      autoRepeat: fetchFilteredAutoRepeatData,
      fullAppraisal: fetchFilteredFullAppraisalData,
      topUp: fetchFilteredTopUpData,
    };

    const columnName = state.selectedColumns[type].toLowerCase();
    const trimmedInput = state.searchInputs[type].trim();

    dispatch(
      filterActions[type]({
        jwtBearer,
        filters: { [columnName]: trimmedInput },
      })
    );

    setState((prevState) => ({
      ...prevState,
      hasSearched: {
        ...prevState.hasSearched,
        [type]: true,
      },

      pages: {
        ...prevState.pages,
        [type]: { ...prevState.pages[type], page: 1 },
      },
    }));
  };

  // const handleReset = (type) => {
  //   setState(prevState => ({
  //     ...prevState,
  //     searchInputs: {
  //       ...prevState.searchInputs,
  //       [type]: '',
  //     },
  //     selectedColumns: {
  //       ...prevState.selectedColumns,
  //       [type]: '',
  //     },
  //     hasSearched: {
  //       ...prevState.hasSearched,
  //       [type]: false,
  //     },
  //     filters: {
  //       ...prevState.filters,
  //       [type]: {},
  //     },
  //   }));

  //   if (type === 'autoRepeat') {
  //     dispatch(fetchAutoRepeatData({ jwtBearer, page: state.pages.autoRepeat.page, filters: {}, pageSize: state.pages.autoRepeat.pageSize }));
  //   } else if (type === 'fullAppraisal') {
  //     dispatch(fetchFullAppraisalData({ jwtBearer, page: state.pages.fullAppraisal.page, filters: {}, pageSize: state.pages.fullAppraisal.pageSize }));
  //   } else if (type === 'topUp') {
  //     dispatch(fetchTopUpData({ jwtBearer, page: state.pages.topUp.page, filters: {}, pageSize: state.pages.topUp.pageSize }));
  //   }

  //   setTriggerFilter(type);
  // };

  const handleReset = (type) => {
    trackFeatureAction("Reports", "Repeat Eligibility", "reset");
    console.log("Before reset:", state);

    setState((prevState) => {
      const newState = {
        ...prevState,
        searchInputs: {
          ...prevState.searchInputs,
          [type]: "",
        },
        selectedColumns: {
          ...prevState.selectedColumns,
          [type]: "",
        },
      };

      console.log("State after reset:", newState);
      return newState;
    });

    setTriggerFilter(type);
  };
  useEffect(() => {
    if (triggerFilter) {
      handleFilter(triggerFilter);
      setTriggerFilter(null);
    }
  }, [triggerFilter]);

  const paginationConfig = (type) => {
    const total = {
      autoRepeat: state.hasSearched.autoRepeat ? filteredAutoRepeatData.length : totalCountAutoRepeat,
      fullAppraisal: state.hasSearched.fullAppraisal ? filteredFullAppraisalData.length : totalCountFullAppraisal,
      topUp: state.hasSearched.topUp ? filteredTopUpData.length : totalCountTopUp,
    }[type];

    return {
      current: state.pages[type].page,
      pageSize: state.pages[type].pageSize,
      total: total,
      showTotal: (total, [start, end]) => `${start}-${end} of ${total} items`,
      onChange: (page, pageSize) => setState(prevState => ({
        ...prevState,
        pages: { ...prevState.pages, [type]: { page, pageSize } },
      })),
    };
  };

  const renderSearchBar = (columns, type) => (
    <Space style={{ marginBottom: 16, marginLeft: 2 }}>
      <Select
        placeholder="Select Column"
        style={{ width: 200, height: 32, lineHeight: '32px' }}
        value={state.selectedColumns[type] || undefined}
        onChange={(value) =>
          setState((prevState) => ({
            ...prevState,
            selectedColumns: {
              ...prevState.selectedColumns,
              [type]: value,
            },
          }))
        }
      >
        {columns.map((col) => (
          <Option key={col.key} value={col.dataIndex}>
            {col.title}
          </Option>
        ))}
      </Select>
      <Input
        placeholder="Value"
        value={state.searchInputs[type]}
        onChange={(e) =>
          setState((prevState) => ({
            ...prevState,
            searchInputs: {
              ...prevState.searchInputs,
              [type]: e.target.value,
            },
          }))
        }
        onKeyDown={(e) => handleKeyDown(e, type)}
        style={{ width: 150, height: 32, lineHeight: '32px' }}
        disabled={!state.selectedColumns[type]}
      />
      <Button
        type="primary"
        onClick={() => handleFilter(type)}
        style={{ height: 32, lineHeight: '32px' }}
      >
        Search
      </Button>
      {(filteredAutoRepeatData || filteredFullAppraisalData || filteredTopUpData) && (
        <Button onClick={() => handleReset(type)} style={{ width: 70, height: 32, lineHeight: '32px' }}>
          Reset
        </Button>
      )}
    </Space>
  );

  const handleAPIDownload = (type) => {
    dispatch(downloadRepeatEligibilityReport({ jwtBearer, type }));
  };

  const renderButtonByType = (type) => (
    <StyledButton type="primary" onClick={() => handleAPIDownload(type)} loading={type === "autoRepeat" ? isDownloadingAuto : type === "fullAppraisal" ? isDownloadingFull : type === "topUp" ? isDownloadingTop : null}>
      Download Report
    </StyledButton>
  );

  const getCurrentMonthName = () => {
    const date = new Date();
    const options = { month: 'long' };
    return new Intl.DateTimeFormat('en-IN', options).format(date);
  };

  const currentMonth = getCurrentMonthName();

  return (
    <div>
      {["autoRepeat", "fullAppraisal", "topUp"].map((type) => {
        const data = {
          autoRepeat: state.hasSearched.autoRepeat ? filteredAutoRepeatData : autoRepeatData,
          fullAppraisal: state.hasSearched.fullAppraisal ? filteredFullAppraisalData : fullAppraisalData,
          topUp: state.hasSearched.topUp ? filteredTopUpData : topUpData,
        }[type];
        const columns = defineColumns(data || [], type);

        const isLoading = {
          autoRepeat: isLoadingAutoRepeat,
          fullAppraisal: isLoadingFullAppraisal,
          topUp: isLoadingTopUp,
        }[type];
        const error = {
          autoRepeat: errorAutoRepeat,
          fullAppraisal: errorFullAppraisal,
          topUp: errorTopUp,
        }[type];

        const isLoadingFiltered = {
          autoRepeat: isLoadingFilteredAutoRepeat,
          fullAppraisal: isLoadingFilteredFullAppraisal,
          topUp: isLoadingFilteredTopUp,
        }[type];

        return (
          <Card
            key={type}
            title={<span style={{ fontWeight: 600, fontSize: '24px', fontFamily: 'poppins-font, sans-serif' }}>
              {type === 'autoRepeat' ? 'Pre Approved' : type === 'fullAppraisal' ? 'Full Appraisal' : 'Top Up'}
            </span>}
            bordered
            extra={
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                {renderButtonByType(type)}
              </div>
            }
          >
            {renderSearchBar(columns, type)}
            <p style={{ fontSize: "18px", fontWeight: 500, marginTop: "8px" }}>
              The OSP shown is as of 3rd of {currentMonth}. Actual OSP will be calculated at the time of disbursement.
            </p>
            {isLoadingFiltered || isLoading ? (
              <div className="spinner-container">
                <HashLoader color="#f57171" />
              </div>
            ) : error ? (
              <Alert message={error} type="error" />
            ) : (
              <StyledTable
                columns={columns}
                dataSource={data}
                pagination={paginationConfig(type)}
                locale={{ emptyText: <Empty description="No data available" /> }}
                scroll={isAdmin && (type === "fullAppraisal" || type === "topUp") ? { x: "max-content" } : undefined}
              />
            )}
          </Card>
        );
      })}

      <Modal
        title="Update Servicing BA"
        open={baModal.open}
        onCancel={handleCloseBAModal}
        onOk={handleSubmitBA}
        okText="Update"
        confirmLoading={isUpdatingServicingBA}
        width={420}
        destroyOnClose
      >
        <Form layout="vertical">
          <Form.Item label="Loan Ext ID">
            <Input value={getLoanExtId(baModal.record) || ""} disabled />
          </Form.Item>
          <Form.Item label="Select Servicing BA" required>
            <Select
              showSearch
              placeholder={isLoadingServicingBAList ? "Loading..." : "Select BA"}
              value={selectedBA}
              onChange={setSelectedBA}
              loading={isLoadingServicingBAList}
              disabled={isLoadingServicingBAList}
              style={{ width: "100%" }}
              optionFilterProp="children"
            >
              {servicingBAList.map((ba) => (
                <Option key={ba} value={ba}>{ba}</Option>
              ))}
            </Select>
            {errorServicingBAList && (
              <span style={{ color: "red", fontSize: 12 }}>{errorServicingBAList}</span>
            )}
          </Form.Item>
          <Form.Item label="Remarks">
            <Input.TextArea
              rows={3}
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Optional remarks"
            />
          </Form.Item>
          {updateServicingBAError && (
            <Alert type="error" message={updateServicingBAError} />
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default RepeatEligibility;
